import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { QueryStatus, authAxios } from "../../utils";
import { RootState } from "../../redux/store";
import {
  ConnectVehiclesToProviderPayload,
  CreateAnalyticVehiclesPayload,
  VehicleApi,
} from "./telemetryApi";

export interface VehiclesOfACompanyState {
  analyticVehiclesListOfACompanyStatus: QueryStatus;
  analyticVehiclesListOfACompany: VehicleApi[];
  createAnalyticVehiclesStatus: QueryStatus;
  connectVehiclesToProviderStatus: QueryStatus;
}

const initialState: VehiclesOfACompanyState = {
  analyticVehiclesListOfACompanyStatus: "idle",
  analyticVehiclesListOfACompany: [],
  createAnalyticVehiclesStatus: "idle",
  connectVehiclesToProviderStatus: "idle",
};

export const analyticVehiclesListOfACompanyAsync = createAsyncThunk(
  "telemetry/analyticVehiclesListOfACompany/call",
  async (payload: { companyUuid: string }) => {
    const axios = authAxios();
    const response = await axios.get<VehicleApi[]>(
      `/telemetry/vehicles/company/${payload.companyUuid}`,
    );
    return response.data.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  },
);

export const createAnalyticVehiclesToCheckTheirEgibilitiesAsync =
  createAsyncThunk(
    "telemetry/createAnalyticVehiclesToCheckTheirEgibilities/call",
    async (payload: CreateAnalyticVehiclesPayload) => {
      const axios = authAxios();
      await axios.post(`/telemetry/vehicles/create_analytic_vehicles`, payload);
      return;
    },
  );

export const connectVehiclesToProviderAsync = createAsyncThunk(
  "telemetry/connectVehiclesToProvider/call",
  async (payload: ConnectVehiclesToProviderPayload) => {
    const axios = authAxios();
    await axios.post(
      `/telemetry/vehicles/connect_vehicles_to_provider`,
      payload,
    );
    return;
  },
);

export const telemetrySlice = createSlice({
  name: "telemetry",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {},
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(analyticVehiclesListOfACompanyAsync.pending, (state) => {
        state.analyticVehiclesListOfACompanyStatus = "processing";
      })
      .addCase(
        analyticVehiclesListOfACompanyAsync.fulfilled,
        (state, action) => {
          state.analyticVehiclesListOfACompany = action.payload;
          state.analyticVehiclesListOfACompanyStatus = "success";
        },
      )
      .addCase(analyticVehiclesListOfACompanyAsync.rejected, (state) => {
        state.analyticVehiclesListOfACompanyStatus = "failed";
      })
      .addCase(
        createAnalyticVehiclesToCheckTheirEgibilitiesAsync.pending,
        (state) => {
          state.createAnalyticVehiclesStatus = "processing";
        },
      )
      .addCase(
        createAnalyticVehiclesToCheckTheirEgibilitiesAsync.fulfilled,
        (state) => {
          state.createAnalyticVehiclesStatus = "success";
        },
      )
      .addCase(
        createAnalyticVehiclesToCheckTheirEgibilitiesAsync.rejected,
        (state) => {
          state.createAnalyticVehiclesStatus = "failed";
        },
      )
      .addCase(connectVehiclesToProviderAsync.pending, (state) => {
        state.connectVehiclesToProviderStatus = "processing";
      })
      .addCase(connectVehiclesToProviderAsync.fulfilled, (state) => {
        state.connectVehiclesToProviderStatus = "success";
      })
      .addCase(connectVehiclesToProviderAsync.rejected, (state) => {
        state.connectVehiclesToProviderStatus = "failed";
      });
  },
});

export const selectAnalyticVehiclesListOfACompany = (state: RootState) =>
  state.telemetry.analyticVehiclesListOfACompany;
export const selectAnalyticVehiclesListOfACompanyStatus = (state: RootState) =>
  state.telemetry.analyticVehiclesListOfACompanyStatus;
export const selectCreateAnalyticVehiclesStatus = (state: RootState) =>
  state.telemetry.createAnalyticVehiclesStatus;
export const selectConnectVehiclesToProviderStatus = (state: RootState) =>
  state.telemetry.connectVehiclesToProviderStatus;

export default telemetrySlice.reducer;
